import type { Attempt, FluencyAttempt, InFlightQuiz } from '@/types';

const NS = 'fitbcba';
const ATTEMPTS_KEY = (unitId: string) => `${NS}.attempts.${unitId}`;
const FLUENCY_KEY = (courseSlug: string) => `${NS}.fluency.${courseSlug}`;
const SESSION_KEY = `${NS}.session`;

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try { return JSON.parse(raw) as T; } catch { return null; }
}

export const attemptStore = {
  list(unitId: string): Attempt[] {
    return safeParse<Attempt[]>(localStorage.getItem(ATTEMPTS_KEY(unitId))) ?? [];
  },
  append(attempt: Attempt) {
    const list = this.list(attempt.unitId);
    list.unshift(attempt);
    localStorage.setItem(ATTEMPTS_KEY(attempt.unitId), JSON.stringify(list.slice(0, 50)));
  },
  bestScore(
    unitId: string,
    filter?: (a: Attempt) => boolean,
  ): { pct: number; score: number; total: number } | null {
    const list = this.list(unitId);
    const filtered = filter ? list.filter(filter) : list;
    if (filtered.length === 0) return null;
    const best = filtered.reduce((a, b) => (b.score / b.total > a.score / a.total ? b : a));
    return { pct: Math.round((best.score / best.total) * 100), score: best.score, total: best.total };
  },
};

export const sessionStore = {
  get(): InFlightQuiz | null {
    return safeParse<InFlightQuiz>(sessionStorage.getItem(SESSION_KEY));
  },
  set(q: InFlightQuiz) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(q));
  },
  clear() {
    sessionStorage.removeItem(SESSION_KEY);
  },
};

export const lastAttemptStore = {
  get(unitId: string): Attempt | null {
    const list = attemptStore.list(unitId);
    return list[0] ?? null;
  },
};

export const fluencyAttemptStore = {
  list(courseSlug: string): FluencyAttempt[] {
    return safeParse<FluencyAttempt[]>(localStorage.getItem(FLUENCY_KEY(courseSlug))) ?? [];
  },
  append(attempt: FluencyAttempt) {
    const list = this.list(attempt.courseSlug);
    list.unshift(attempt);
    localStorage.setItem(FLUENCY_KEY(attempt.courseSlug), JSON.stringify(list.slice(0, 50)));
  },
  bestScore(courseSlug: string): { pct: number; score: number; total: number } | null {
    const list = this.list(courseSlug);
    if (list.length === 0) return null;
    const best = list.reduce((a, b) => (b.score / b.total > a.score / a.total ? b : a));
    return { pct: Math.round((best.score / best.total) * 100), score: best.score, total: best.total };
  },
};
